/**
 * @license
 * Copyright 2018-2021 Streamlit Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export const colors = {
  transparent: "transparent",
  current: "currentColor",
  inherit: "inherit",
  black: "#000000",
  white: "#FFFFFF",
  streamlitPink: "#f63366",

  gray10: "#fafafa",
  gray20: "#f0f2f6",
  gray30: "#e6eaf1",
  gray40: "#d5dae5",
  gray50: "#bfc5d3",
  gray60: "#a3a8b8",
  gray70: "#808495",
  gray80: "#555867",
  gray85: "#31333F",
  gray90: "#262730",
  gray100: "#0e1117",

  red10: "#fff0f0",
  red20: "#ffdede",
  red30: "#ffc7c7",
  red40: "#ffabab",
  red50: "#ff8c8c",
  red60: "#ff6c6c",
  red70: "#ff4b4b",
  red80: "#ff2b2b",
  red90: "#bd4043",
  red100: "#7d353b",

  orange10: "#fffae8",
  orange20: "#fff6d0",
  orange30: "#ffecb0",
  orange40: "#ffe08e",
  orange50: "#ffd16a",
  orange60: "#ffbd45",
  orange70: "#ffa421",
  orange80: "#ff8700",
  orange90: "#ed6f13",
  orange100: "#d95a00",

  yellow10: "#ffffe1",
  yellow20: "#ffffc2",
  yellow30: "#ffffa0",
  yellow40: "#ffff7d",
  yellow50: "#ffff59",
  yellow60: "#fff835",
  yellow70: "#ffe312",
  yellow80: "#faca2b",
  yellow90: "#edbb16",
  yellow100: "#dea816",
  yellow110: "#916e10",

  green10: "#dffde9",
  green20: "#c0fcd3",
  green30: "#9ef6bb",
  green40: "#7defa1",
  green50: "#5ce488",
  green60: "#3dd56d",
  green70: "#21c354",
  green80: "#09ab3b",
  green90: "#158237",
  green100: "#177233",

  blueGreen10: "#dcfffb",
  blueGreen20: "#bafff7",
  blueGreen30: "#93ffee",
  blueGreen40: "#6bfde3",
  blueGreen50: "#45f4d5",
  blueGreen60: "#20e7c5",
  blueGreen70: "#00d4b1",
  blueGreen80: "#29b09d",
  blueGreen90: "#2c867c",
  blueGreen100: "#246e69",

  lightBlue10: "#e0feff",
  lightBlue20: "#bffdff",
  lightBlue30: "#9af8ff",
  lightBlue40: "#73efff",
  lightBlue50: "#4be4ff",
  lightBlue60: "#24d4ff",
  lightBlue70: "#00c0f2",
  lightBlue80: "#00a4d4",
  lightBlue90: "#0d8cb5",
  lightBlue100: "#15799e",

  blue10: "#e4f5ff",
  blue20: "#c7ebff",
  blue30: "#a6dcff",
  blue40: "#83c9ff",
  blue50: "#60b4ff",
  blue60: "#3d9df3",
  blue70: "#1c83e1",
  blue80: "#0068c9",
  blue90: "#0054a3",
  blue100: "#004280",

  purple10: "#f5ebff",
  purple20: "#ebd6ff",
  purple30: "#dbbbff",
  purple40: "#c89dff",
  purple50: "#b27eff",
  purple60: "#9a5dff",
  purple70: "#803df5",
  purple80: "#6d3fc0",
  purple90: "#583f84",
  purple100: "#3f3163",
}

export const tailwindColors = {
  blueGray50: "#f8fafc",
  blueGray100: "#f1f5f9",
  blueGray200: "#e2e8f0",
  blueGray300: "#cbd5e1",
  blueGray400: "#94a3b8",
  blueGray500: "#64748b",
  blueGray600: "#475569",
  blueGray700: "#334155",
  blueGray800: "#1e293b",
  blueGray900: "#0f172a",

  gray50: "#f9fafb",
  gray100: "#f3f4f6",
  gray200: "#e5e7eb",
  gray300: "#d1d5db",
  gray400: "#9ca3af",
  gray500: "#6b7280",
  gray600: "#4b5563",
  gray700: "#374151",
  gray800: "#1f2937",
  gray900: "#111827",

  trueGray50: "#fafafa",
  trueGray100: "#f5f5f5",
  trueGray200: "#e5e5e5",
  trueGray300: "#d4d4d4",
  trueGray400: "#a3a3a3",
  trueGray500: "#737373",
  trueGray600: "#525252",
  trueGray700: "#404040",
  trueGray800: "#262626",
  trueGray900: "#171717",

  warmGray50: "#fafaf9",
  warmGray100: "#f5f5f4",
  warmGray200: "#e7e5e4",
  warmGray300: "#d6d3d1",
  warmGray400: "#a8a29e",
  warmGray500: "#78716c",
  warmGray600: "#57534e",
  warmGray700: "#44403c",
  warmGray800: "#292524",
  warmGray900: "#1c1917",

  red50: "#fef2f2",
  red100: "#fee2e2",
  red200: "#fecaca",
  red300: "#fca5a5",
  red400: "#f87171",
  red500: "#ef4444",
  red600: "#dc2626",
  red700: "#b91c1c",
  red800: "#991b1b",
  red900: "#7f1d1d",

  orange50: "#fff7ed",
  orange100: "#ffedd5",
  orange200: "#fed7aa",
  orange300: "#fdba74",
  orange400: "#fb923c",
  orange500: "#f97316",
  orange600: "#ea580c",
  orange700: "#c2410c",
  orange800: "#9a3412",
  orange900: "#7c2d12",

  yellow50: "#fffbeb",
  yellow100: "#fef3c7",
  yellow200: "#fde68a",
  yellow300: "#fcd34d",
  yellow400: "#fbbf24",
  yellow500: "#f59e0b",
  yellow600: "#d97706",
  yellow700: "#b45309",
  yellow800: "#92400e",
  yellow900: "#78350f",

  lime50: "#f7fee7",
  lime100: "#ecfccb",
  lime200: "#d9f99d",
  lime300: "#bef264",
  lime400: "#a3e635",
  lime500: "#84cc16",
  lime600: "#65a30d",
  lime700: "#4d7c0f",
  lime800: "#3f6212",
  lime900: "#365314",

  green50: "#ecfdf5",
  green100: "#d1fae5",
  green200: "#a7f3d0",
  green300: "#6ee7b7",
  green400: "#34d399",
  green500: "#10b981",
  green600: "#059669",
  green700: "#047857",
  green800: "#065f46",
  green900: "#064e3b",

  teal50: "#f0fdfa",
  teal100: "#ccfbf1",
  teal200: "#99f6e4",
  teal300: "#5eead4",
  teal400: "#2dd4bf",
  teal500: "#14b8a6",
  teal600: "#0d9488",
  teal700: "#0f766e",
  teal800: "#115e59",
  teal900: "#134e4a",

  cyan50: "#ecfeff",
  cyan100: "#cffafe",
  cyan200: "#a5f3fc",
  cyan300: "#67e8f9",
  cyan400: "#22d3ee",
  cyan500: "#06b6d4",
  cyan600: "#0891b2",
  cyan700: "#0e7490",
  cyan800: "#155e75",
  cyan900: "#164e63",

  lightBlue50: "#f0f9ff",
  lightBlue100: "#e0f2fe",
  lightBlue200: "#bae6fd",
  lightBlue300: "#7dd3fc",
  lightBlue400: "#38bdf8",
  lightBlue500: "#0ea5e9",
  lightBlue600: "#0284c7",
  lightBlue700: "#0369a1",
  lightBlue800: "#075985",
  lightBlue900: "#0c4a6e",

  blue50: "#eff6ff",
  blue100: "#dbeafe",
  blue200: "#bfdbfe",
  blue300: "#93c5fd",
  blue400: "#60a5fa",
  blue500: "#3b82f6",
  blue600: "#2563eb",
  blue700: "#1d4ed8",
  blue800: "#1e40af",
  blue900: "#1e3a8a",

  indigo50: "#eef2ff",
  indigo100: "#e0e7ff",
  indigo200: "#c7d2fe",
  indigo300: "#a5b4fc",
  indigo400: "#818cf8",
  indigo500: "#6366f1",
  indigo600: "#4f46e5",
  indigo700: "#4338ca",
  indigo800: "#3730a3",
  indigo900: "#312e81",

  purple50: "#f5f3ff",
  purple100: "#ede9fe",
  purple200: "#ddd6fe",
  purple300: "#c4b5fd",
  purple400: "#a78bfa",
  purple500: "#8b5cf6",
  purple600: "#7c3aed",
  purple700: "#6d28d9",
  purple800: "#5b21b6",
  purple900: "#4c1d95",

  fuchsia50: "#fdf4ff",
  fuchsia100: "#fae8ff",
  fuchsia200: "#f5d0fe",
  fuchsia300: "#f0abfc",
  fuchsia400: "#e879f9",
  fuchsia500: "#d946ef",
  fuchsia600: "#c026d3",
  fuchsia700: "#a21caf",
  fuchsia800: "#86198f",
  fuchsia900: "#701a75",

  pink50: "#fdf2f8",
  pink100: "#fce7f3",
  pink200: "#fbcfe8",
  pink300: "#f9a8d4",
  pink400: "#f472b6",
  pink500: "#ec4899",
  pink600: "#db2777",
  pink700: "#be185d",
  pink800: "#9d174d",
  pink900: "#831843",

  rose50: "#fff1f2",
  rose100: "#ffe4e6",
  rose200: "#fecdd3",
  rose300: "#fda4af",
  rose400: "#fb7185",
  rose500: "#f43f5e",
  rose600: "#e11d48",
  rose700: "#be123c",
  rose800: "#9f1239",
  rose900: "#881337",
}
